import {
	ChangeDetectionStrategy,
	Component,
	Input,
	OnInit,
} from '@angular/core';
import { openClose } from '@core/animations/animations';
import { isGuarantor } from '@core/util/client';
import { capitalize } from '@core/util/string';
import { ClientType } from '@models/clients';
import {
	BusinessMatterClientWithAssociations,
	MatterClientType,
	PersonMatterClientWithAssociations,
} from '@models/matter';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ViewSelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Select } from '@ngxs/store';
import { MatterClients } from '@state/global/matter_clients';
import { MatterMortgagesState } from '@state/matter/matter_mortgages';
import { MatterClientDispatch } from '@state/matter_clients/matterClients.dispatch';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
	DropdownOption,
	DropdownResult,
} from 'src/app/shared/atomic/atoms/dropdown-options/dropdown-options.component';
import { BusinessState } from '@state/business';
import { DrawerService } from 'src/app/ui/drawer/drawer.service';
import { GlobalProfileComponent } from 'src/app/private/global-profile/global-profile.component';
import { GlobalProfileNavType } from 'src/app/private/global-profile/global-profile.types';
import { YES_NO_OPTIONS } from 'src/app/ui/utils/constants';
import { BaseComponent } from '@core/base.component';
import { getDisplayName } from '@core/util/name-formatting.utils';
import { TestHooks } from './client-tab.hooks';

enum ClientKebabOptionTypes {
	GLOBALVIEW = 'globalView',
	REMOVE = 'remove',
	PRIMARY = 'primary',
}

const ClientKebabOptions: DropdownOption<ClientKebabOptionTypes>[] = [
	{
		name: 'View global profile',
		value: ClientKebabOptionTypes.GLOBALVIEW,
		classes: ['text-right'],
	},
	{
		name: 'Make primary contact',
		value: ClientKebabOptionTypes.PRIMARY,
		classes: ['text-right'],
	},
	{
		name: 'Remove',
		value: ClientKebabOptionTypes.REMOVE,
		classes: ['text-red', 'text-right'],
	},
];

export const SpouseKebabOptions: DropdownOption<ClientKebabOptionTypes>[] = [
	{ name: 'View global profile', value: ClientKebabOptionTypes.GLOBALVIEW },
	{ name: 'Remove spouse', value: ClientKebabOptionTypes.REMOVE },
];

export interface VM {
	_client:
		| PersonMatterClientWithAssociations
		| BusinessMatterClientWithAssociations;
	guarantorMortgagesNames: string[];
	matterClients: (
		| PersonMatterClientWithAssociations
		| BusinessMatterClientWithAssociations
	)[];
	afterFirstLoad: boolean;
	isGuarantor: boolean;
	associatedContactsButtonText?: string;
	hasAssociatedContacts?: boolean;
}

export const defaultVM: VM = {
	_client: null,
	guarantorMortgagesNames: [],
	matterClients: [],
	afterFirstLoad: false,
	isGuarantor: false,
	associatedContactsButtonText: '',
	hasAssociatedContacts: false,
};

@UntilDestroy()
@Component({
	selector: 'app-client-tab',
	templateUrl: './client-tab.component.html',
	animations: [openClose()],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class ClientTabComponent extends BaseComponent<VM> implements OnInit {
	testHooks = TestHooks;
	public clientTypes = ClientType;
	public clientKebabOptions = ClientKebabOptions;
	public yesNoOptions = YES_NO_OPTIONS;

	@Input() set client(
		client:
			| PersonMatterClientWithAssociations
			| BusinessMatterClientWithAssociations,
	) {
		this.VM = {
			_client: client,
		};

		if (client?.clientAssociations?.length > 0)
			this.setAssociatedContactVM(client);
		else
			this.VM = {
				hasAssociatedContacts: false,
			};

		if (client?.id && client?.id !== this.VM._client?.id) {
			this.getGuarantorMortgagesNames(client?.id).subscribe((mortgages) => {
				this.VM = {
					guarantorMortgagesNames: mortgages,
				};
			});
		}

		this.VM = {
			isGuarantor: isGuarantor(this.VM._client),
			afterFirstLoad: true,
		};
	}
	@Input() clientType: MatterClientType;

	@ViewSelectSnapshot(BusinessState.getTreefortConnected)
	isTreefortConnected: boolean;

	@Select(MatterClients.get_matterClientsWithAssociationMapped)
	matterClients$: Observable<
		(
			| PersonMatterClientWithAssociations
			| BusinessMatterClientWithAssociations
		)[]
	>;

	@Select(MatterMortgagesState.getMortgagesNamesByGuarantor)
	mortgagesNames$: Observable<(clientId: string) => string[]>;

	constructor(
		private matterClientDispatch: MatterClientDispatch,
		private drawerService: DrawerService,
	) {
		super(defaultVM);
	}

	ngOnInit(): void {
		this.matterClients$
			.pipe(untilDestroyed(this))
			.subscribe((_matterClients) => {
				this.VM = {
					matterClients: _matterClients,
				};
			});
	}

	getGuarantorMortgagesNames(clientId: string): Observable<string[]> {
		return this.mortgagesNames$.pipe(
			untilDestroyed(this),
			map((fltr) => fltr(clientId)),
		);
	}

	viewAssociatedContacts() {
		this.drawerService.openDrawer(GlobalProfileComponent, {
			title: 'Global Profile',
			data: {
				clientId: this.VM._client.id,
				tab: GlobalProfileNavType.ASSOCIATED_CONTACTS,
			},
		});
	}

	onClientKebabSelect(
		$event: DropdownResult<DropdownOption<ClientKebabOptionTypes>>,
	) {
		switch ($event.result.value) {
			case ClientKebabOptionTypes.GLOBALVIEW:
				this.drawerService.openDrawer(GlobalProfileComponent, {
					title: 'Global Profile',
					data: { clientId: this.VM._client.id },
				});
				break;
			case ClientKebabOptionTypes.REMOVE:
				this.removeClient(this.VM._client);
				break;
			case ClientKebabOptionTypes.PRIMARY:
				this.matterClientDispatch.SetIsPrimary(this.VM._client.id, true);
		}
	}

	private removeClient(
		client:
			| PersonMatterClientWithAssociations
			| BusinessMatterClientWithAssociations,
	) {
		this.matterClientDispatch.RemoveClient(client.id, this.clientType);
	}

	getClientPills(
		client:
			| PersonMatterClientWithAssociations
			| BusinessMatterClientWithAssociations,
	): string[] {
		const pills: string[] = [];
		if (client?.primaryContact) {
			pills.push('PRIMARY CONTACT');
		}

		client?.clientTypes?.forEach((type) => {
			pills.push(capitalize(type));
		});

		return pills;
	}

	setAssociatedContactVM(
		client:
			| PersonMatterClientWithAssociations
			| BusinessMatterClientWithAssociations,
	): void {
		const [primaryAssociation, ...remainingAssociations] =
			client.clientAssociations;
		const associatedClient = primaryAssociation.client;
		const additionalContactsCount = remainingAssociations.length;

		const buttonText =
			getDisplayName(associatedClient) +
			(additionalContactsCount > 0 ? ` +${additionalContactsCount}` : '');

		this.VM = {
			associatedContactsButtonText: buttonText,
			hasAssociatedContacts: true,
		};
	}
}
